import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { Button } from "@eika/button";
import Spinner from '../../common/Spinner/Spinner';
import Message from '../../common/Message/Message';
import { useTranslation, useActivation } from '../../common/util/Hook';
import { StartActivationResult, SendPasswordParam, SendPasswordResult } from '../../generated-types';
import { postSendPassword, setSendPassword } from '../../actions/activation';

import './SendPassordPaSMS.scss';
import { formatTranslatedText } from '../../common/util/Functions';
import { Translations } from '../../types';

export interface SendPassordPaSMSProps {
    namespace?: string;
    standAlone?: boolean;
    activation?: StartActivationResult;
}

interface State {
    dispatch: (s: any) => void;
    status: number;
    translated: Translations;
    message: string;
    error: string;
}

const sendPasswod = (props: SendPassordPaSMSProps, state: State, setState: (s: State) => void, e: any): void => {
    if (e && typeof (e.preventDefault) === 'function') {
        e.preventDefault();
    }

    if (state.status) {
        return;
    }

    const param: SendPasswordParam = {
        reference: props.activation?.reference || '',
    };

    state.dispatch(setSendPassword(undefined));
    setState({...state, status: 1});
    state.dispatch(postSendPassword(param));
};

const updateState = (props: SendPassordPaSMSProps, state: State, setState: (s: State) => void, passwordSending?: SendPasswordResult): void => {
    const update = {...state, error: '', message: ''};
    const defaultText = state.translated.opsNoeGikkGalt;
    if (passwordSending === null && !state.error) {
        update.error = defaultText;
    } else if (passwordSending?.success === false && !state.error) {
        update.error = state.translated[passwordSending?.result] || defaultText;
    } else if (passwordSending?.success === true && !state.message) {
        update.message = formatTranslatedText([props.activation?.phoneNumberHint || '*** ** ***'], false, state.translated.bankIdAvtaleSendPassordPaSMSerSendt);
    }

    if ((update.message || update.error) && update.status) {
        setState({...update, status: 2});
        if (update.error) {
            setTimeout(() => {
                setState({...update, message: '', error: '', status: 0});
            }, (1000 * 10));
        }
    }
};

const SendPassordPaSMS: React.FC<SendPassordPaSMSProps> = (props): React.ReactElement | null => {
    const dispatch = useDispatch();
    const translated = useTranslation();
    const {passwordSending} = useActivation();
    const [state, setState] = useState<State>({
        translated,
        dispatch,
        status: 0,
        message: '',
        error: '',
    });

    useEffect(() => {
        updateState(props, state, setState, passwordSending);
    }, [props, state, setState, passwordSending]);

    return props.activation?.reference && props.activation?.phoneNumberHint ? (
        <section aria-labelledby="send-password-to-sms-link" className={classNames('send-passord-pa-sms-wrapper', props.namespace, {
            '-on-sending': state.status === 1,
            '-is-sent': state.status === 2,
            'stand-alone-wrapper': !!props.standAlone,
        })}>
            {state.status === 0 && <Button
                role="button"
                id="send-password-to-sms-button"
                className="bank-id-link -send-sms"
                onClick={(e: any) => {
                    sendPasswod(props, state, setState, e);
                }}>
                {translated.bankIdAvtaleSendPassordPaSMS}
            </Button>}
            {state.status === 1 && <Spinner namespace="on-sending-spinner"/>}
            {state.status === 2 &&
                <Message type={state.message ? 'info' : 'error'} polite namespace="sent-message" text={state.message || state.error}/>}
        </section>
    ) : null;
};

export default SendPassordPaSMS;
